// ModelPrices.tsx — what each AI model costs KontractAI, editable by an operator.
//
// WHY THIS SCREEN EXISTS. Every spend figure in the console read $0.00 because
// the price list was empty. The rollup joins prices with a LEFT JOIN and
// COALESCE(...,0) so that an unpriced model still records its usage rather than
// being dropped — the right trade, but it means "no price on file" and "this
// cost nothing" render identically. There was also no way to enter a price
// except by hand in the database, so the first correct rate change would have
// needed an engineer. Both are fixed here.
//
// THESE ARE OUR COSTS, NOT CUSTOMER BILLING. The figures are the providers'
// published per-token rates for the exact model the router dispatches. Customer
// pricing lives on the Billing screen and is a separate number.
//
// THE LIST IS APPEND-ONLY, ON PURPOSE. Saving a price does not overwrite the old
// one; it records a new rate starting from a date. Spend already reported stays
// costed at the rate that was actually in force, so a rate change today cannot
// silently rewrite last month's figures. It also means a rate you know is coming
// can be entered now with its future start date and will simply take effect.
//
// SCOPED TO `PT`. No import from theme.ts.

import React, { useCallback, useEffect, useState } from "react";
import { pget, pmutate, perr } from "./api";
import { PT, PT_NUM } from "../../platformTheme";
import { PCard, PBtn, PBanner, PSpinner, PReasonPrompt } from "./ui";

interface PriceRow {
  provider: string;
  model: string;
  input_cost_per_mtok: string;
  output_cost_per_mtok: string;
  currency: string;
  effective_from: string;
}
interface Unpriced { provider: string; model: string; display_name?: string }

const th: React.CSSProperties = {
  textAlign: "left", fontSize: 11, fontWeight: 600, color: PT.txM,
  textTransform: "uppercase", letterSpacing: 0.4, padding: "8px 12px",
  borderBottom: `1px solid ${PT.brd}`, whiteSpace: "nowrap",
};
const td: React.CSSProperties = {
  padding: "8px 12px", fontSize: 12.5, borderBottom: `1px solid ${PT.brd}`, verticalAlign: "middle",
};
const input: React.CSSProperties = {
  width: 92, padding: "4px 7px", fontSize: 12.5, ...PT_NUM, textAlign: "right",
  borderRadius: 6, border: `1px solid ${PT.brd}`, background: PT.bg, color: PT.tx,
};

const money = (v: string | number) => {
  const n = Number(v);
  return Number.isFinite(n) ? `$${n.toFixed(n < 1 ? 4 : 2)}` : "—";
};
const day = (iso: string) => (iso ? String(iso).slice(0, 10) : "—");

export default function ModelPrices() {
  const [prices, setPrices] = useState<PriceRow[] | null>(null);
  const [scheduled, setScheduled] = useState<PriceRow[]>([]);
  const [unpriced, setUnpriced] = useState<Unpriced[]>([]);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);

  // Keyed by "provider|model" so an edit in progress survives a re-render and
  // two models with the same short name cannot collide.
  const [edit, setEdit] = useState<Record<string, { input: string; output: string; from: string }>>({});
  const [prompt, setPrompt] = useState<null | { title: string; detail: React.ReactNode; run: (reason: string) => Promise<void> }>(null);
  const [pBusy, setPBusy] = useState(false);
  const [pErr, setPErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    const r = await pget("/ai-prices");
    if (!r.ok) { setErr(perr(r, "Could not load the price list")); setPrices([]); }
    else {
      setPrices(r.data?.prices || []);
      setScheduled(r.data?.scheduled || []);
      setUnpriced(r.data?.configured_without_price || []);
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const keyOf = (p: { provider: string; model: string }) => `${p.provider}|${p.model}`;

  const save = async (provider: string, model: string, reason: string) => {
    const e = edit[`${provider}|${model}`];
    const r = await pmutate("PUT", "/ai-prices", reason, {
      provider, model,
      input_cost_per_mtok: Number(e.input),
      output_cost_per_mtok: Number(e.output),
      effective_from: e.from || undefined,
      source: "Entered on the Usage & Spend screen from the provider's published rate card",
    });
    if (!r.ok) throw new Error(perr(r, "Could not save the price"));
    setMsg(`Saved ${provider} ${model} — ${money(e.input)} in / ${money(e.output)} out per million tokens, from ${e.from || "now"}.`);
    setEdit((s) => { const n = { ...s }; delete n[`${provider}|${model}`]; return n; });
    await load();
  };

  const rows: Array<PriceRow | Unpriced> = [
    ...(prices || []),
    // A configured model with NO price is listed in the same table rather than
    // in a footnote — it is the one row an operator actually has to act on, and
    // burying it is how the $0.00 went unexplained for as long as it did.
    ...unpriced,
  ].sort((a, b) => (a.provider + a.model).localeCompare(b.provider + b.model));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <PCard>
        {/* PCard's `title` prop is an HTML tooltip attribute, not a heading —
            passing a heading to it renders nothing visible. */}
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>What each AI model costs us</div>
        <div style={{ fontSize: 12, color: PT.txM, marginBottom: 10 }}>
          The providers' published per-token rates, used to work out spend. This is our cost, not
          what customers pay.
        </div>
        <PBanner>{err}</PBanner>
        <PBanner tone="ok">{msg}</PBanner>

        {unpriced.length > 0 && (
          <PBanner tone="warn">
            {unpriced.length} model{unpriced.length === 1 ? " is" : "s are"} in use with no price on
            file. Their calls are counted but shown as costing nothing, which understates spend.
            Enter a rate below.
          </PBanner>
        )}

        {loading ? <PSpinner /> : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Provider</th>
                  <th style={th}>Model</th>
                  <th style={{ ...th, textAlign: "right" }}>Per 1M input tokens</th>
                  <th style={{ ...th, textAlign: "right" }}>Per 1M output tokens</th>
                  <th style={th}>In force since</th>
                  <th style={th} />
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const k = keyOf(p);
                  const priced = "input_cost_per_mtok" in p;
                  const e = edit[k];
                  return (
                    <tr key={k}>
                      <td style={{ ...td, fontWeight: 600 }}>{p.provider}</td>
                      <td style={{ ...td, ...PT_NUM, fontSize: 11.5 }}>{p.model}</td>
                      <td style={{ ...td, textAlign: "right" }}>
                        {e ? (
                          <input style={input} value={e.input} inputMode="decimal"
                            onChange={(ev) => setEdit((s) => ({ ...s, [k]: { ...s[k], input: ev.target.value } }))} />
                        ) : priced ? (
                          <span style={PT_NUM}>{money((p as PriceRow).input_cost_per_mtok)}</span>
                        ) : (
                          <span style={{ color: PT.warn, fontWeight: 600 }}>not set</span>
                        )}
                      </td>
                      <td style={{ ...td, textAlign: "right" }}>
                        {e ? (
                          <input style={input} value={e.output} inputMode="decimal"
                            onChange={(ev) => setEdit((s) => ({ ...s, [k]: { ...s[k], output: ev.target.value } }))} />
                        ) : priced ? (
                          <span style={PT_NUM}>{money((p as PriceRow).output_cost_per_mtok)}</span>
                        ) : (
                          <span style={{ color: PT.warn, fontWeight: 600 }}>not set</span>
                        )}
                      </td>
                      <td style={{ ...td, color: PT.txM }}>
                        {e ? (
                          <input style={{ ...input, width: 118, textAlign: "left" }} type="date" value={e.from}
                            onChange={(ev) => setEdit((s) => ({ ...s, [k]: { ...s[k], from: ev.target.value } }))} />
                        ) : priced ? day((p as PriceRow).effective_from) : "—"}
                      </td>
                      <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                        {e ? (
                          <>
                            <PBtn small onClick={() => setPrompt({
                              title: `Set the price for ${p.provider} ${p.model}`,
                              detail: (
                                <>
                                  Records a new rate of {money(e.input)} per million input tokens and{" "}
                                  {money(e.output)} per million output tokens, starting {e.from || "today"}.
                                  <div style={{ marginTop: 7, color: PT.txM }}>
                                    The previous rate is kept. Spend already reported stays costed at the
                                    rate that was in force at the time, so this does not change past figures.
                                  </div>
                                </>
                              ),
                              run: (reason) => save(p.provider, p.model, reason),
                            })}>Save</PBtn>
                            <PBtn small v="ghost"
                              onClick={() => setEdit((s) => { const n = { ...s }; delete n[k]; return n; })}>
                              Cancel
                            </PBtn>
                          </>
                        ) : (
                          <PBtn small v="ghost" onClick={() => setEdit((s) => ({
                            ...s,
                            [k]: {
                              input: priced ? String(Number((p as PriceRow).input_cost_per_mtok)) : "",
                              output: priced ? String(Number((p as PriceRow).output_cost_per_mtok)) : "",
                              from: new Date().toISOString().slice(0, 10),
                            },
                          }))}>
                            {priced ? "Change" : "Set price"}
                          </PBtn>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {scheduled.length > 0 && (
          <div style={{ marginTop: 12, fontSize: 12, color: PT.txM }}>
            <strong style={{ color: PT.tx }}>Rate changes already scheduled</strong>
            <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
              {scheduled.map((s, i) => (
                <li key={i} style={{ marginBottom: 2 }}>
                  {s.provider} {s.model} moves to {money(s.input_cost_per_mtok)} in /{" "}
                  {money(s.output_cost_per_mtok)} out per million tokens on {day(s.effective_from)}.
                </li>
              ))}
            </ul>
          </div>
        )}

        <div style={{ marginTop: 12, fontSize: 11.5, color: PT.txM, lineHeight: 1.5 }}>
          Cost is worked out per call as input tokens × the input rate plus output tokens × the
          output rate, so a model that is cheap to send to and expensive to read back is costed
          correctly. Rates are per million tokens, which is how every provider publishes them.
        </div>
      </PCard>

      <PReasonPrompt
        open={!!prompt}
        title={prompt?.title || ""}
        detail={prompt?.detail}
        busy={pBusy}
        error={pErr}
        onCancel={() => setPrompt(null)}
        onConfirm={async (reason) => {
          if (!prompt) return;
          setPBusy(true); setPErr("");
          try { await prompt.run(reason); setPrompt(null); }
          catch (e: any) { setPErr(e?.message || "Could not save the price"); }
          finally { setPBusy(false); }
        }}
      />
    </div>
  );
}
